"use client";

import { createPortal } from "react-dom";
import { Check, ChevronUp, ChevronDown, Compass, Sparkles, Grid3x3, Play, Zap, LayoutGrid, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useOnboardingMode, GEX_STAGES, GEX_STAGES_WITH_HUB, gexStageLabel } from "@/hooks/use-onboarding-mode";
import { useFeatureFlag } from "@/hooks/use-feature-flags";
import { useState, useEffect, useRef } from "react";

const STAGE_ICONS: Record<string, React.ElementType> = {
  hub: Compass,
  "gex-ai": Sparkles,
  heatmap: Grid3x3,
  replay: Play,
  "gamma-flip": Zap,
  workspace: LayoutGrid,
};

const STAGE_HINTS: Record<string, string> = {
  hub: "See where gamma is moving today (delayed data).",
  "gex-ai": "Type a ticker to analyze gamma exposure with AI.",
  heatmap: "See gamma walls across top tickers at a glance.",
  replay: "Hit Play to see how GEX levels evolved intraday.",
  "gamma-flip": "Explore gamma flip events and see when the market shifted.",
  workspace: "See a prebuilt multi-chart workspace with SPY & GLD.",
};

const STAGE_HIGHLIGHT_TARGETS: Record<string, string> = {
  hub: "gex-hub-overview",
  "gex-ai": "gex-ai-ticker",
  heatmap: "gex-heatmap-grid",
  replay: "replay-load-button",
  "gamma-flip": "gamma-flip-events",
  workspace: "workspace-demo-grid",
};

export function GexOnboardingProgress() {
  const { isGexOnboarding, gexStage, gexCompletedStages, advanceGexStage, completeGexOnboarding, showGexIntro, dismissGexIntro, goBackGexStage, gexTourCompleted, setShowPricing, isFreeUser, isSpecialUser, isGexOnly, switchToWheelTour } = useOnboardingMode();
  const gexHubEnabled = useFeatureFlag("gexHub");
  const gexStages = gexHubEnabled ? GEX_STAGES_WITH_HUB : GEX_STAGES;
  const [collapsed, setCollapsed] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [showContinuePulse, setShowContinuePulse] = useState(false);
  // True once the pulse target has been located & highlighted for the current stage.
  // Used to gate the Continue button: while a target is blinking, user must click it
  // before they can advance. Falls back to enabled if no target ever appears.
  const [pulseTargetFound, setPulseTargetFound] = useState(false);
  const continueBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    if (showGexIntro) return;
    const targetName = STAGE_HIGHLIGHT_TARGETS[gexStage];
    if (!targetName) return;

    let observer: MutationObserver | null = null;
    let interval: ReturnType<typeof setInterval> | null = null;
    let dismissed = false;

    const applyPulse = () => {
      if (dismissed) return;
      const el = document.querySelector(`[data-shepherd-highlight-target="${targetName}"]`);
      document.querySelectorAll('.onboarding-pulse').forEach(old => {
        if (old !== el) old.classList.remove('onboarding-pulse');
      });
      if (el && !el.classList.contains('onboarding-pulse')) {
        el.classList.add('onboarding-pulse');
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setPulseTargetFound(true);
      }
    };

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const pulseEl = document.querySelector(`[data-shepherd-highlight-target="${targetName}"]`);
      if (pulseEl && (pulseEl === target || pulseEl.contains(target))) {
        dismissed = true;
        pulseEl.classList.remove('onboarding-pulse');
        setShowContinuePulse(true);
      }
    };

    interval = setInterval(applyPulse, 1000);
    observer = new MutationObserver(applyPulse);
    observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('click', handleClick, true);

    return () => {
      dismissed = true;
      if (interval) clearInterval(interval);
      if (observer) observer.disconnect();
      document.removeEventListener('click', handleClick, true);
      document.querySelectorAll('.onboarding-pulse').forEach(el => el.classList.remove('onboarding-pulse'));
    };
  }, [gexStage, showGexIntro]);

  useEffect(() => {
    setShowContinuePulse(false);
    setPulseTargetFound(false);
  }, [gexStage]);

  if (!isGexOnboarding || !mounted) return null;

  if (showGexIntro) {
    const introPanel = (
      <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="w-full max-w-md rounded-lg overflow-hidden shadow-xl animate-in fade-in zoom-in-95 duration-200">
          <div className="h-1.5 bg-gradient-to-r from-transparent via-zinc-300 dark:via-zinc-600 to-transparent" />
          <div className="border border-t-0 border-border/50 bg-background rounded-b-lg">
            <div className="p-5 sm:p-8 space-y-5">
              <div className="space-y-2">
                <h2 className="text-lg font-semibold tracking-tight">
                  Welcome to GEX
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Use gamma exposure data to understand where market makers are positioned and how price may move. This guide will show you:
                </p>
              </div>

              <div className="space-y-2.5">
                {[
                  { icon: Sparkles, label: "GEX AI", desc: "Analyze gamma exposure with AI-powered insights" },
                  ...(gexHubEnabled
                    ? [{ icon: Compass, label: "GEX Hub", desc: "See where gamma is moving today (delayed data)" }]
                    : []),
                  { icon: Grid3x3, label: "GEX Heatmap", desc: "See gamma walls across top tickers at a glance" },
                  { icon: Play, label: "GEX Replay", desc: "Replay intraday GEX changes alongside price action" },
                  { icon: Zap, label: "Gamma Flip", desc: "Track when market gamma flips between zones" },
                  { icon: LayoutGrid, label: "Workspace", desc: "Multi-chart dashboard with gamma, vanna & charm" },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-zinc-500/10 text-zinc-600 dark:text-zinc-300">
                      <item.icon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{item.label}</p>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                size="lg"
                className="w-full h-11 bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:opacity-90 shadow-sm font-semibold"
                onClick={dismissGexIntro}
              >
                Start Tour
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
    return typeof document !== "undefined" ? createPortal(introPanel, document.body) : null;
  }

  const currentIdx = Math.max(0, gexStages.indexOf(gexStage));
  const nextLabel = currentIdx < gexStages.length - 1 ? gexStageLabel(gexStages[currentIdx + 1]) : null;

  const panel = (
    <div
      className="fixed z-[10001] bottom-0 left-0 right-0 sm:bottom-4 sm:left-4 sm:right-auto sm:w-72 sm:rounded-lg rounded-t-xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
      role="complementary"
      aria-label="GEX onboarding progress"
    >
      <div className="h-1 bg-gradient-to-r from-transparent via-zinc-300 dark:via-zinc-600 to-transparent" />
      <div className="sm:rounded-b-lg border border-t-0 border-border/50 bg-background shadow-lg">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left"
          aria-expanded={!collapsed}
        >
          <span className="text-sm font-semibold truncate">
            {gexTourCompleted ? "GEX Tour Complete" : `GEX Tour \u2014 ${currentIdx + 1}/${gexStages.length}`}
            {!gexTourCompleted && (() => { const t = gexHubEnabled ? ["~2m", "~1m", "~1m", "~1:30", "~1m", "~30s"] : ["~2m", "~1m", "~1:30", "~1m", "~30s"]; return t[currentIdx] ? <span className="text-[10px] font-normal text-muted-foreground/60 ml-1.5">{t[currentIdx]}</span> : null; })()}
          </span>
          {collapsed ? <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />}
        </button>

        {!collapsed && (
          <div className="px-4 pb-4 space-y-3 border-t border-border/30 pt-3">
            <div className="relative">
              {gexStages.map((stage, i) => {
                const done = gexCompletedStages.includes(stage);
                const current = stage === gexStage;
                const Icon = STAGE_ICONS[stage] || Sparkles;
                const isLast = i === gexStages.length - 1;

                return (
                  <div key={stage} className="flex items-start gap-3 relative">
                    {!isLast && (
                      <div className={`absolute left-[13px] top-[30px] w-0.5 h-[calc(100%-20px)] ${done ? "bg-emerald-500/40" : "bg-border"}`} />
                    )}
                    <div className={`
                      relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-all
                      ${done ? "bg-emerald-500/20 text-emerald-500" : current ? "bg-foreground text-background shadow-sm" : "bg-muted text-muted-foreground"}
                    `}>
                      {done ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                    </div>
                    <div className={`pb-3 ${isLast ? "pb-0" : ""}`}>
                      <p className={`text-sm leading-tight ${current ? "font-semibold text-foreground" : done ? "text-muted-foreground" : "text-muted-foreground/60"}`}>
                        {i + 1}. {gexStageLabel(stage)}
                      </p>
                      {current && (
                        <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                          {STAGE_HINTS[stage]}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-2">
              {currentIdx > 0 && !gexTourCompleted && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="shrink-0 text-muted-foreground"
                  onClick={() => { setShowContinuePulse(false); goBackGexStage(); }}
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                </Button>
              )}
              {gexTourCompleted ? (
                <>
                  {currentIdx > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="shrink-0 text-muted-foreground"
                      onClick={() => goBackGexStage()}
                    >
                      <ArrowLeft className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:opacity-90 shadow-sm"
                    onClick={() => setShowPricing(true)}
                  >
                    Upgrade
                    <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                  </Button>
                </>
              ) : (() => {
                const waitingForTargetClick = pulseTargetFound && !showContinuePulse;
                return (
                  <Button
                    ref={continueBtnRef}
                    size="sm"
                    disabled={waitingForTargetClick}
                    title={waitingForTargetClick ? "Click the highlighted item first to continue" : undefined}
                    className={`flex-1 bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:opacity-90 shadow-sm ${showContinuePulse ? "onboarding-pulse-btn" : ""} disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none`}
                    onClick={() => { setShowContinuePulse(false); advanceGexStage(); }}
                  >
                    {currentIdx >= gexStages.length - 1
                      ? "Complete Tour"
                      : nextLabel || "Continue"}
                    <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                  </Button>
                );
              })()}
            </div>
            {!isGexOnly && !gexTourCompleted && (
              <button
                type="button"
                className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors pt-1"
                onClick={switchToWheelTour}
              >
                Looking for the Wheel? <span className="underline underline-offset-2">Switch to the PRO tour</span>
              </button>
            )}
            {(!isFreeUser || isSpecialUser) && !gexTourCompleted && (
              <button
                type="button"
                className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
                onClick={completeGexOnboarding}
              >
                Skip tour &mdash; I&apos;ll explore on my own
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(panel, document.body) : null;
}
